import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  BookOpen,
  Calendar,
  User,
  DollarSign,
  CheckCircle,
  Clock,
  AlertCircle,
  ArrowLeft,
  FileText,
  GraduationCap,
  MessageSquare,
  Video,
  CreditCard,
  Users
} from 'lucide-react';
import axios from 'axios';
import { getImageUrl } from '../utils/imageUtils';

const UnitDetails = () => {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [unit, setUnit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [curriculum, setCurriculum] = useState([]);
  const [fetchingCurriculum, setFetchingCurriculum] = useState(false);

  useEffect(() => {
    checkActiveSession();
    // Poll for active session every 30 seconds
    const interval = setInterval(checkActiveSession, 30000);
    return () => clearInterval(interval);
  }, [unitId]);

  const checkActiveSession = async () => {
    try {
      const response = await axios.get('/livekit/active-sessions');
      const sessions = response.data;
      const session = sessions.find(s => s.unitId === parseInt(unitId) && s.isActive);
      setActiveSession(session);
    } catch (error) {
      console.error('Error checking active session:', error);
    }
  };

  const handleJoinVirtualClass = async () => {
    // Payment Check
    if (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid') {
      alert('Access to virtual classes requires a confirmed payment for this unit.');
      return;
    }

    if (!activeSession) {
      // Double check just in case
      try {
        const response = await axios.get('/livekit/active-sessions');
        const sessions = response.data;
        const session = sessions.find(s => s.unitId === parseInt(unitId) && s.isActive);
        if (session) {
          navigate('/virtual-classroom');
          return;
        }
      } catch (e) { }

      alert('No active virtual class is currently available for this unit.');
      return;
    }
    navigate('/virtual-classroom');
  };
  useEffect(() => {
    // Check if unit data was passed via navigation state
    if (location.state?.unit) {
      setUnit(location.state.unit);
      setLoading(false);
      // Still fetch updated payment status if needed
      fetchUpdatedUnitData(location.state.unit);
      fetchCurriculumData(unitId);
    } else {
      // Fallback: fetch unit details if not passed
      fetchUnitDetails();
      fetchCurriculumData(unitId);
    }
  }, [unitId, location.state]);

  const fetchUnitDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      // First, try to find the unit in student's enrolled units
      const studentUnitsResponse = await axios.get('/student-units');
      // DEBUG: Log the raw response and the search ID
      console.log('DEBUG: Finding UnitID:', unitId, 'in', studentUnitsResponse.data);

      const enrolledUnit = studentUnitsResponse.data.find(u => {
        // Try strict equality first, then loose
        const idMatch = (u.unit?.id || u.Unit?.id) == unitId || (u.unitId) == unitId;
        return idMatch;
      });

      console.log('DEBUG: Found Enrolled Unit:', enrolledUnit);

      if (enrolledUnit) {
        // Handle case-sensitivity mismatch dynamically
        const unitData = enrolledUnit.unit || enrolledUnit.Unit || {};

        // Unit is enrolled - use enrolled unit data
        setUnit({
          ...unitData,
          status: enrolledUnit.status,
          paymentStatus: enrolledUnit.paymentStatus,
          studentUnitId: enrolledUnit.id,
          cost: unitData.cost,
          currency: unitData.currency,
          lecturer: enrolledUnit.AssignedTutor || unitData.lecturer, // Use assigned tutor first
          course: unitData.Course,
          completionVote: enrolledUnit.completionVote,
          rejectionReason: enrolledUnit.rejectionReason,
          isRetake: enrolledUnit.isRetake,
          history: enrolledUnit.history
        });
      } else {
        // Unit is not enrolled - fetch from public units
        const publicUnitResponse = await axios.get(`/units/${unitId}`);
        if (publicUnitResponse.data) {
          // Get student details for course and campus info
          const studentResponse = await axios.get(`/students/${user.id}?includeCourse=true&includeCampus=true`);
          const studentData = studentResponse.data;

          // Get cost information for the unit
          let costData = null;
          try {
            const costResponse = await axios.get(`/unit-costs/current/${unitId}/${studentData.campusId}`);
            costData = costResponse.data;
          } catch (error) {
            console.log('Cost not available for this unit');
          }

          setUnit({
            ...publicUnitResponse.data,
            status: publicUnitResponse.data.status,
            paymentStatus: 'not_enrolled',
            studentUnitId: null,
            cost: costData?.cost,
            currency: costData?.currency,
            lecturer: publicUnitResponse.data.status === 'active' || publicUnitResponse.data.status === 'completed'
              ? publicUnitResponse.data.lecturer
              : null,
            course: studentData.course
          });
        } else {
          setError('Unit not found');
        }
      }
    } catch (error) {
      console.error('Error fetching unit details:', error);
      setError('Failed to load unit details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCurriculumData = async (uid) => {
    try {
      setFetchingCurriculum(true);
      const res = await axios.get(`/lesson-plans/${uid}`);
      setCurriculum(res.data);
    } catch (err) {
      console.error('Error fetching curriculum:', err);
    } finally {
      setFetchingCurriculum(false);
    }
  };

  const handleToggleStep = async (itemId, completed) => {
    try {
      await axios.post('/lesson-plans/toggle-progress', { lessonPlanItemId: itemId, completed });
      setCurriculum(prev => prev.map(item => item.id === itemId ? { ...item, completed } : item));
    } catch (err) {
      console.error('Error toggling step:', err);
    }
  };

  const fetchUpdatedUnitData = async (existingUnit) => {
    try {
      // Fetch updated payment status for the unit
      const studentUnitsResponse = await axios.get('/student-units');
      const enrolledUnit = studentUnitsResponse.data.find(u => {
        const idMatch = (u.unit?.id || u.Unit?.id) == unitId || (u.unitId) == unitId;
        return idMatch;
      });

      if (enrolledUnit) {
        // Handle case-sensitivity dynamically
        const unitData = enrolledUnit.unit || enrolledUnit.Unit || {};

        // FULL UPDATE: Merge backend data with existing state to ensure cohort status is present
        setUnit(prevUnit => ({
          ...prevUnit,
          ...unitData, // Overwrite with fresh backend data (e.g. currentCohortStatus)
          paymentStatus: enrolledUnit.paymentStatus,
          status: enrolledUnit.status,
          studentUnitId: enrolledUnit.id,
          completionVote: enrolledUnit.completionVote,
          rejectionReason: enrolledUnit.rejectionReason,
          isRetake: enrolledUnit.isRetake,
          history: enrolledUnit.history,
          // Preservation of some frontend-calculated fields if needed, but fresh data is preferred
          cost: unitData.cost || prevUnit.cost,
          currency: unitData.currency || prevUnit.currency
        }));
      }
    } catch (error) {
      console.error('Error fetching updated unit data:', error);
      // Don't set error state for this - it's just an update
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      case 'upcoming': return 'text-orange-600 bg-orange-100';
      case 'in-progress': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in-progress': return 'In Progress';
      case 'active': return 'Active';
      case 'upcoming': return 'Upcoming';
      default: return 'Pending';
    }
  };

  const handleDownloadUnitTranscript = async () => {
    try {
      setDownloading(true);
      const response = await axios.get(`/student-units/${unitId}/unit-transcript`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `transcript_${unit?.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading unit transcript:', error);
      alert('Failed to download unit transcript. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleAction = () => {
    if (unit.status === 'completed') {
      handleDownloadUnitTranscript();
    } else if (unit.status === 'active' || unit.status === 'upcoming' || (unit.course?.selfPacedEnabled && unit.status === 'published') || (unit.course?.selfPacedEnabled && unit.status === 'enrolled')) {
      // Check for forming status first
      if (unit.currentCohortStatus === 'forming') {
        alert('Class is still forming. You will be notified when it starts.');
        return;
      }

      if (unit.course?.selfPacedEnabled) {
        // Payment Check for self-paced action
        if (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid') {
          alert('Please complete payment for this unit to access learning materials.');
          navigate(`/payment/${unit.id}`);
          return;
        }
        // Future: Navigate to learning content directly
        navigate(`/learning-materials?unitId=${unit.id}`);
      } else {
        handleJoinVirtualClass();
      }
    }
  };

  const handlePayment = () => {
    navigate(`/payment/${unit.id}`);
  };

  const handleCastVote = async (vote, reason = null) => {
    try {
      const response = await axios.post('/student-units/vote-completion', {
        unitId: unit.unitId || unit.id, // Handle likely ID mismatch structure if checking raw unit vs enrolled
        vote,
        reason
      });
      alert(response.data.message);
      fetchUnitDetails(); // Refresh
    } catch (err) {
      console.error(err);
      alert('Failed to cast vote.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error || !unit) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-16 h-16 text-red-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Unit</h3>
        <p className="text-gray-600 mb-4">{error || 'Unit not found'}</p>
        <button
          onClick={() => navigate('/units', { state: { fromCourse: location.state?.fromCourse, fromFilter: location.state?.fromFilter } })}
          className="btn-primary"
        >
          Back to Units
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/units', { state: { fromCourse: location.state?.fromCourse, fromFilter: location.state?.fromFilter } })}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Unit Details</h1>
          <p className="text-gray-600">Manage your unit enrollment and access resources</p>
        </div>
      </div>

      {/* Unit Header */}
      <div className="card relative overflow-hidden">
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${getStatusColor(unit.status).split(' ')[1]}`} />
        <div className="p-6 pl-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-xl font-bold text-gray-900">{unit.name}</h2>
                {unit.isRetake && (
                  <span className="bg-orange-100 text-orange-800 text-[10px] font-bold px-2 py-0.5 rounded-full border border-orange-200 uppercase tracking-tighter">
                    🔄 Retake
                  </span>
                )}
              </div>
              <p className="text-gray-600 mb-4">{unit.description || 'No description available'}</p>
            </div>
            <span className={`status-badge ${getStatusColor(unit.status)}`}>
              {getStatusText(unit.status)}
            </span>
          </div>
        </div>
      </div>

      {/* Graduation Status & Retake Option */}
      {unit.status === 'completed' ? (
        <div className="rounded-lg p-6 border bg-blue-50 border-blue-200 text-blue-900 mb-6 shadow-sm">
          <div className="flex flex-col items-center text-center">
            <GraduationCap className="w-16 h-16 text-blue-600 mb-4" />
            <h3 className="text-2xl font-bold mb-2">Congratulations!</h3>
            <p className="text-lg mb-6">
              You have successfully completed <strong>{unit.name}</strong>.
            </p>

            <div className="flex flex-wrap justify-center gap-4 w-full max-w-lg">
              <button
                onClick={() => handleDownloadUnitTranscript(unitId, unit.name)}
                className="flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-sm"
              >
                <FileText className="w-5 h-5 mr-2" />
                Download Certificate
              </button>

              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to retake this unit? This will reset your progress and you will need to re-enroll and pay again.')) {
                    // Need to implement handleRetakeUnit here or navigate to units page
                    axios.post(`/student-units/${user.id}/${unitId}/retake`, {}, {
                      headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                      }
                    }).then(() => {
                      alert('Unit reset successfully! You can now re-enroll.');
                      // Navigate back to units list
                      // Navigate back to units list with state
                      navigate('/units', { state: { fromCourse: location.state?.fromCourse, fromFilter: location.state?.fromFilter } });
                    }).catch(err => {
                      console.error('Error resetting unit:', err);
                      alert('Failed to reset unit.');
                    });
                  }
                }}
                className="flex items-center justify-center px-6 py-3 bg-white border-2 border-blue-600 text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
              >
                🔄 Retake Unit
              </button>
            </div>

            <p className="text-sm text-blue-700 mt-6 italic">
              Note: Retaking this unit will reset your progress and require new enrollment payment.
            </p>
          </div>
        </div>
      ) : (
        /* Cohort Status Alert & Voting (Self-Paced) */
        unit.currentCohortStatus && (
          <div className={`rounded-lg p-4 border ${unit.currentCohortStatus === 'active' ? 'bg-green-50 border-green-200 text-green-800' :
            unit.currentCohortStatus === 'pending_conclusion' ? 'bg-purple-50 border-purple-200 text-purple-800' :
              'bg-blue-50 border-blue-200 text-blue-800'
            }`}>
            <div className="flex flex-col gap-3">
              <div className="flex items-center">
                {unit.currentCohortStatus === 'active' ? <CheckCircle className="w-5 h-5 mr-3" /> :
                  unit.currentCohortStatus === 'pending_conclusion' ? <AlertCircle className="w-5 h-5 mr-3" /> :
                    <Users className="w-5 h-5 mr-3" />}
                <div>
                  <h3 className="font-bold">
                    {unit.currentCohortStatus === 'active' ? 'Class In Session' :
                      unit.currentCohortStatus === 'pending_conclusion' ? 'Class Conclusion Requested' :
                        'Class Forming'}
                  </h3>
                  <p className="text-sm mt-1">
                    {unit.currentCohortStatus === 'active' ? 'This class is currently active.' :
                      unit.currentCohortStatus === 'pending_conclusion' ? 'The tutor has requested to end this class. Please vote below.' :
                        `${unit.enrolledCount || 0} of ${unit.minClassSize || 1} joined.`}
                  </p>
                </div>
              </div>

              {/* Voting Section */}
              {unit.currentCohortStatus === 'pending_conclusion' && unit.completionVote && (
                <div className="bg-white rounded-lg p-4 mt-2">
                  {unit.completionVote === 'pending' ? (
                    <>
                      <p className="font-medium text-gray-900 mb-2">Did this class meet your expectations?</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleCastVote('granted')}
                          className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
                        >
                          Yes, Grant Completion
                        </button>
                        <button
                          onClick={() => {
                            const reason = prompt("Please provide a reason for rejection:");
                            if (reason) handleCastVote('rejected', reason);
                          }}
                          className="flex-1 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
                        >
                          No, Reject
                        </button>
                      </div>
                    </>
                  ) : unit.completionVote === 'rejected' ? (
                    <div className="bg-red-50 p-3 rounded border border-red-100">
                      <p className="font-semibold text-red-800 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-2" />
                        Feedback Submitted
                      </p>
                      <p className="text-sm text-red-700 mt-1">
                        You have voted <strong>No</strong>.
                      </p>
                      <p className="text-xs text-red-600 mt-1 italic">
                        "Waiting for tutor response regarding: {unit.rejectionReason}"
                      </p>
                    </div>
                  ) : (
                    <div className="bg-green-50 p-3 rounded border border-green-100">
                      <p className="font-semibold text-green-800 flex items-center">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Completion Granted
                      </p>
                      <p className="text-sm text-green-700 mt-1">
                        You have approved this class. Waiting for final graduation by the tutor.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}

      {/* Unit Content Checklist & Progress */}
      {curriculum.length > 0 && (
        <div className="card mb-6 overflow-hidden animate-in fade-in slide-in-from-top-4 duration-500 shadow-lg border-blue-100">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 border-b border-blue-100 flex justify-between items-center">
            <div>
              <h3 className="font-bold text-blue-900 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-blue-600" />
                Your Learning Path
              </h3>
              <p className="text-[11px] text-blue-700 font-medium">Step-by-step curriculum prepared by your tutor.</p>
            </div>
            <div className="text-right">
              <span className="text-lg font-black text-blue-900">{Math.round((curriculum.filter(i => i.isCompleted).length / (curriculum.length || 1)) * 100)}%</span>
              <p className="text-[10px] text-blue-600 font-bold uppercase tracking-wider">Completed</p>
            </div>
          </div>

          <div className="w-full bg-gray-100 h-2.5">
            <div
              className="bg-gradient-to-r from-blue-600 to-indigo-600 h-full transition-all duration-700 ease-out shadow-[0_0_15px_rgba(37,99,235,0.4)]"
              style={{ width: `${(curriculum.filter(i => i.isCompleted).length / (curriculum.length || 1)) * 100}%` }}
            />
          </div>

          <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {curriculum.map((item, index) => (
              <div
                key={item.id}
                className={`flex items-start p-4 rounded-xl border-2 transition-all ${item.isLive
                  ? 'bg-gradient-to-r from-orange-50 to-amber-50 border-orange-400 shadow-lg shadow-orange-200 animate-pulse ring-2 ring-orange-300'
                  : item.isCompleted
                    ? 'bg-green-50 border-green-200 shadow-sm'
                    : 'bg-white border-gray-100'
                  }`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center mr-4 transition-all flex-shrink-0 mt-0.5 ${item.isLive
                  ? 'bg-orange-600 text-white shadow-lg shadow-orange-300'
                  : item.isCompleted
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-100 text-gray-400'
                  }`}>
                  {item.isLive ? '🔴' : item.isCompleted ? <CheckCircle className="w-5 h-5" /> : <span className="text-xs font-black">{index + 1}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={`text-sm font-bold truncate transition-colors ${item.isLive ? 'text-orange-900' :
                      item.isCompleted ? 'text-green-800' :
                        'text-gray-900'
                      }`}>
                      {item.title}
                    </h4>
                    {item.isLive && (
                      <span className="text-[10px] bg-orange-600 text-white px-2 py-0.5 rounded-full font-black uppercase tracking-wide animate-pulse flex items-center gap-1">
                        🔴 LIVE NOW
                      </span>
                    )}
                    {item.isCompleted && <span className="text-[10px] bg-green-200 text-green-700 px-1.5 py-0.5 rounded font-bold uppercase">Done</span>}
                  </div>
                  <p className={`text-xs leading-relaxed transition-all ${item.isLive ? 'text-orange-700 font-medium' :
                    item.isCompleted ? 'text-green-600' :
                      'text-gray-500'
                    }`}>
                    {item.isLive ? '🎯 Your tutor is teaching this lesson right now!' : (item.description || 'No detailed description provided.')}
                  </p>

                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <span className="text-[9px] font-black uppercase tracking-widest text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">
                      {item.type.replace('_', ' ')}
                    </span>
                    {item.isLive && item.liveStartedAt ? (
                      <span className="text-[10px] text-orange-700 font-bold flex items-center gap-1 bg-orange-100 px-2 py-0.5 rounded">
                        <div className="w-1.5 h-1.5 rounded-full bg-orange-600 animate-pulse" />
                        Started at {new Date(item.liveStartedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    ) : item.isCompleted && item.completedAt ? (
                      <span className="text-[10px] text-green-600 font-bold flex items-center gap-1">
                        <div className="w-1 h-1 rounded-full bg-green-600" />
                        Finished on {new Date(item.completedAt).toLocaleDateString()}
                      </span>
                    ) : item.plannedDate ? (
                      <span className="text-[10px] text-blue-500 font-medium italic">
                        Target: {new Date(item.plannedDate).toLocaleDateString()}
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="card text-center">
          <BookOpen className="w-8 h-8 text-blue-600 mx-auto mb-3" />
          <h3 className="font-medium text-gray-900 mb-1">Course</h3>
          <p className="text-sm text-gray-600">{unit.course?.name || 'TBD'}</p>
        </div>

        <div className="card text-center">
          <Calendar className="w-8 h-8 text-green-600 mx-auto mb-3" />
          <h3 className="font-medium text-gray-900 mb-1">Class Date</h3>
          <p className="text-sm text-gray-600">
            {unit.course?.selfPacedEnabled ? 'Flexible Schedule' : (unit.classDate ? new Date(unit.classDate).toLocaleDateString() : 'TBD')}
          </p>
        </div>

        <div className="card text-center">
          {(unit.Lecturer?.profilePicture || unit.lecturer?.profilePicture) ? (
            <img
              src={getImageUrl(unit.Lecturer?.profilePicture || unit.lecturer?.profilePicture)}
              alt={(unit.Lecturer || unit.lecturer)?.name}
              className="w-12 h-12 rounded-full object-cover border-2 border-white shadow-sm mx-auto mb-3"
            />
          ) : (
            <User className="w-8 h-8 text-purple-600 mx-auto mb-3" />
          )}
          <h3 className="font-medium text-gray-900 mb-1">{unit.isSelfPaced || unit.course?.selfPacedEnabled ? 'Tutor' : 'Lecturer'}</h3>
          <p className="text-sm text-gray-600">{(unit.Lecturer || unit.lecturer)?.name || (unit.isSelfPaced || unit.course?.selfPacedEnabled ? 'Tutor Pool' : 'TBD')}</p>
        </div>

        <div className="card text-center">
          <DollarSign className="w-8 h-8 text-orange-600 mx-auto mb-3" />
          <h3 className="font-medium text-gray-900 mb-1">Cost & Payment</h3>
          <p className="text-sm text-gray-600 mb-2">
            {unit.currency} {unit.cost || 'TBD'}
          </p>
          <p className={`text-sm font-medium ${unit.paymentStatus === 'paid' || unit.paymentStatus === 'confirmed'
            ? 'text-green-600'
            : 'text-orange-600'
            }`}>
            {unit.paymentStatus === 'paid' || unit.paymentStatus === 'confirmed' ? '✓ Paid' : '⏳ Payment Pending'}
          </p>
          {unit.paymentStatus !== 'paid' && unit.paymentStatus !== 'confirmed' && (
            <button
              onClick={handlePayment}
              className="btn-warning mt-2 text-xs px-3 py-1"
            >
              Pay Now
            </button>
          )}
        </div>
      </div>

      {/* Academic Resources */}
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Academic Resources</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => {
              if (unit.enrollmentStatus === 'not_enrolled') {
                alert('Please enroll in this unit first to access learning materials.');
                return;
              }
              if (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid') {
                alert('Please complete payment for this unit to access learning materials.');
                return;
              }
              navigate(`/learning-materials?unitId=${unit.id}`);
            }}
            className={`flex items-center space-x-4 p-4 rounded-lg transition-colors ${unit.enrollmentStatus === 'not_enrolled' || (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid')
              ? 'bg-red-50 hover:bg-red-100 cursor-not-allowed opacity-60'
              : 'bg-gray-50 hover:bg-gray-100'
              }`}
          >
            <div className={`p-3 rounded-lg ${unit.enrollmentStatus === 'not_enrolled' || (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid')
              ? 'bg-red-100'
              : 'bg-blue-100'
              }`}>
              <FileText className={`w-6 h-6 ${unit.enrollmentStatus === 'not_enrolled' || (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid')
                ? 'text-red-600'
                : 'text-blue-600'
                }`} />
            </div>
            <div className="flex-1 text-left">
              <h4 className={`font-medium ${unit.enrollmentStatus === 'not_enrolled' || (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid')
                ? 'text-red-900'
                : 'text-gray-900'
                }`}>
                Learning Materials
              </h4>
              <p className="text-sm text-gray-600">
                {unit.enrollmentStatus === 'not_enrolled'
                  ? 'Enrollment required to access materials'
                  : unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid'
                    ? 'Payment required to access materials'
                    : 'Access lecture notes, slides, and reading materials'
                }
              </p>
            </div>
            {unit.enrollmentStatus === 'not_enrolled' || (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid') ? (
              <div className="text-red-500">
                <AlertCircle className="w-5 h-5" />
              </div>
            ) : null}
          </button>

          <button
            onClick={() => {
              if (unit.enrollmentStatus === 'not_enrolled') {
                alert('Please enroll in this unit first to access assignments and quizzes.');
                return;
              }
              if (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid') {
                alert('Please complete payment for this unit to access assignments and quizzes.');
                return;
              }
              navigate(`/assignments?unitId=${unit.id}`);
            }}
            className={`flex items-center space-x-4 p-4 rounded-lg transition-colors ${unit.enrollmentStatus === 'not_enrolled' || (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid')
              ? 'bg-red-50 hover:bg-red-100 cursor-not-allowed opacity-60'
              : 'bg-gray-50 hover:bg-gray-100'
              }`}
          >
            <div className={`p-3 rounded-lg ${unit.enrollmentStatus === 'not_enrolled' || (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid')
              ? 'bg-red-100'
              : 'bg-green-100'
              }`}>
              <GraduationCap className={`w-6 h-6 ${unit.enrollmentStatus === 'not_enrolled' || (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid')
                ? 'text-red-600'
                : 'text-green-600'
                }`} />
            </div>
            <div className="flex-1 text-left">
              <h4 className={`font-medium ${unit.enrollmentStatus === 'not_enrolled' || (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid')
                ? 'text-red-900'
                : 'text-gray-900'
                }`}>
                Assignments & Quizzes
              </h4>
              <p className="text-sm text-gray-600">
                {unit.enrollmentStatus === 'not_enrolled'
                  ? 'Enrollment required to access assignments'
                  : unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid'
                    ? 'Payment required to access assignments'
                    : 'Submit assignments and take online assessments'
                }
              </p>
            </div>
            {unit.enrollmentStatus === 'not_enrolled' || (unit.paymentStatus !== 'confirmed' && unit.paymentStatus !== 'paid') ? (
              <div className="text-red-500">
                <AlertCircle className="w-5 h-5" />
              </div>
            ) : null}
          </button>

          <button
            onClick={() => alert('Discussion forum feature coming soon!')}
            className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors opacity-60"
          >
            <div className="p-3 bg-purple-100 rounded-lg">
              <MessageSquare className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1 text-left">
              <h4 className="font-medium text-gray-900">Discussion Forum</h4>
              <p className="text-sm text-gray-600">Connect with classmates and ask questions</p>
            </div>
          </button>

          <button
            onClick={() => {
              if (unit.currentCohortStatus === 'forming') {
                alert('Class has not started yet. Waiting for minimum enrollment.');
                return;
              }
              handleJoinVirtualClass();
            }}
            disabled={unit.currentCohortStatus === 'forming'}
            className={`flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors ${(!activeSession || unit.currentCohortStatus === 'forming') ? 'opacity-60 cursor-not-allowed' : 'ring-2 ring-red-500 ring-offset-2'
              }`}
          >
            <div className={`p-3 rounded-lg ${activeSession && unit.currentCohortStatus !== 'forming' ? 'bg-red-100 animate-pulse' : 'bg-gray-200'}`}>
              <Video className={`w-6 h-6 ${activeSession && unit.currentCohortStatus !== 'forming' ? 'text-red-600' : 'text-gray-500'}`} />
            </div>
            <div className="flex-1 text-left">
              <h4 className="font-medium text-gray-900">
                Virtual Classroom
                {activeSession && unit.currentCohortStatus !== 'forming' && <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full animate-pulse">LIVE</span>}
              </h4>
              <p className="text-sm text-gray-600">
                {unit.currentCohortStatus === 'forming'
                  ? 'Class forming - Waiting for start'
                  : activeSession
                    ? 'Class is in session - Join now!'
                    : 'No active session'}
              </p>
            </div>
          </button>
        </div>
      </div>


      {/* Action Buttons */}
      <div className="flex space-x-4">
        {(unit.status === 'completed' || unit.status === 'active' || unit.status === 'upcoming' || (unit.course?.selfPacedEnabled && unit.status === 'published') || (unit.course?.selfPacedEnabled && unit.status === 'enrolled')) && (
          <button
            onClick={handleAction}
            disabled={downloading}
            className={`btn-primary flex-1 ${unit.status === 'completed' ? 'bg-blue-600 hover:bg-blue-700' :
              unit.status === 'active' ? 'bg-green-600 hover:bg-green-700' :
                'bg-orange-600 hover:bg-orange-700'
              }`}
          >
            {unit.status === 'completed'
              ? (downloading ? 'Processing...' : '📄 Download Transcript')
              : (unit.course?.selfPacedEnabled ? '🚀 Start Learning' : '🎯 Join Virtual Class')}
          </button>
        )}
        {unit.status !== 'completed' && unit.history && unit.history.length > 0 && unit.paymentStatus === 'confirmed' && (
          <button
            onClick={handleDownloadUnitTranscript}
            disabled={downloading}
            className="btn-secondary flex-1 flex items-center justify-center border-dashed"
          >
            <FileText className="w-4 h-4 mr-2" />
            {downloading ? 'Downloading...' : 'View Past Transcript'}
          </button>
        )}
      </div>
    </div>
  );
};

export default UnitDetails;
